import { useEffect, useMemo, useState } from "react";
import { CheckCircle, Warning, ArrowsClockwise, Eye, Prohibit, Sparkle, Info, Archive as ArchiveIcon } from "@phosphor-icons/react";
import api from "../lib/api";
import { toast } from "sonner";
import { useDiagnostics, confirmProductionWrite } from "../lib/diagnostics";
import {
  publicMissingReasonLabel,
  publishStatusForCompetition,
} from "../lib/adminPublishStatus";

const STATUS_STYLES = {
  verified_live: "bg-emerald-500/20 text-emerald-300",
  source_pending_review: "bg-amber-500/20 text-amber-300",
  source_unverified: "bg-amber-500/20 text-amber-300",
  expired: "bg-red-500/20 text-red-300",
  broken_link: "bg-red-500/20 text-red-300",
  seed_demo: "bg-slate-500/30 text-slate-200",
  archived: "bg-slate-500/30 text-slate-300",
  hidden: "bg-slate-500/30 text-slate-300",
};

const VALIDATION_STYLES = {
  valid: "bg-emerald-500/20 text-emerald-300",
  homepage_only: "bg-amber-500/20 text-amber-300",
  broken: "bg-red-500/20 text-red-300",
  mismatch: "bg-red-500/20 text-red-300",
  expired: "bg-red-500/20 text-red-300",
  blocked: "bg-red-500/20 text-red-300",
  pending_review: "bg-slate-500/30 text-slate-300",
  redirected: "bg-amber-500/20 text-amber-300",
};

// Iter 20.17.6 — Shared status taxonomy. The `value` MUST be the
// canonical backend `competition_data_status` value (or a stable
// helper key handled by buildAdminQuery). NO display labels in
// `value` — those go in `label`. Card counts + table filters now use
// the same taxonomy.
const FILTERS = [
  { value: "all", label: "All competitions" },
  // Canonical data_status values — mirror visibility_service.py.
  { value: "source_pending_review", label: "Pending review" },
  { value: "verified_live", label: "Verified live" },
  { value: "source_unverified", label: "Source unverified" },
  { value: "expired", label: "Expired" },
  { value: "archived", label: "Archived" },
  { value: "seed_demo", label: "Seed / demo" },
  { value: "hidden", label: "Hidden" },
  { value: "broken_link", label: "Broken link (legacy)" },
  // Helper keys handled by buildAdminQuery.
  { value: "publicly_visible", label: "Publicly visible" },
  { value: "manual_check_only", label: "Needs manual check" },
  // Legacy validation_status filters (kept for older operator URLs).
  { value: "homepage_only", label: "Homepage-only URLs" },
  { value: "mismatch", label: "Domain mismatch" },
  { value: "broken", label: "Broken / 4xx-5xx" },
  // Iter 16 — evidence / readiness oriented filters
  { value: "readiness:ready", label: "Readiness · Ready" },
  { value: "readiness:quick_check", label: "Readiness · Quick check" },
  { value: "readiness:manual_completion", label: "Readiness · Manual completion" },
  { value: "readiness:poor_evidence", label: "Readiness · Poor evidence" },
  { value: "discovery:brave_search", label: "Discovered via Brave Search" },
  { value: "discovery:direct_ingest", label: "Discovered via direct ingest" },
  { value: "discovery:manual", label: "Manually created" },
  { value: "missing:ticket_price", label: "Missing ticket price" },
  { value: "missing:draw_date", label: "Missing draw date" },
  { value: "missing:source_image", label: "Missing source image" },
  { value: "has:ticket_price", label: "Has ticket price" },
  { value: "has:draw_date", label: "Has draw date" },
  { value: "has:source_image", label: "Has source image" },
];

// Status values that map directly to competition_data_status.
const DATA_STATUS_VALUES = new Set([
  "verified_live", "source_pending_review", "source_unverified",
  "expired", "archived", "seed_demo", "hidden", "broken_link",
]);

// Status values that map to source_url_validation_status (legacy).
const VALIDATION_STATUS_VALUES = new Set([
  "homepage_only", "mismatch", "broken",
]);

const SORTS = [
  { value: "newest", label: "Newest first" },
  { value: "readiness_desc", label: "Highest readiness" },
  { value: "completeness_desc", label: "Most complete" },
  { value: "missing_fields_desc", label: "Most missing fields" },
  { value: "operator", label: "By operator" },
];

const READINESS_STYLES = {
  ready: "bg-emerald-500/20 text-emerald-300",
  quick_check: "bg-amber-500/20 text-amber-300",
  manual_completion: "bg-orange-500/20 text-orange-300",
  poor_evidence: "bg-red-500/20 text-red-300",
};

// Map provenance source → short, friendly evidence badge label.
const EVIDENCE_LABELS = {
  brave_snippet: "Brave",
  open_graph: "OG",
  twitter: "Twitter",
  json_ld: "JSON-LD",
  meta_description: "Meta",
  regex_source_page: "Source page",
  manual_admin: "Admin",
};

function buildAdminQuery(filter, sort) {
  const params = new URLSearchParams();
  if (sort && sort !== "newest") params.set("sort", sort);
  if (!filter || filter === "all") return params.toString();
  if (filter.startsWith("readiness:")) {
    params.set("readiness_band", filter.split(":")[1]);
  } else if (filter.startsWith("discovery:")) {
    params.set("discovery_method", filter.split(":")[1]);
  } else if (filter.startsWith("missing:")) {
    const k = filter.split(":")[1];
    if (k === "ticket_price") params.set("has_ticket_price", "false");
    else if (k === "draw_date") params.set("has_draw_date", "false");
    else if (k === "source_image") params.set("has_source_image", "false");
  } else if (filter.startsWith("has:")) {
    const k = filter.split(":")[1];
    if (k === "ticket_price") params.set("has_ticket_price", "true");
    else if (k === "draw_date") params.set("has_draw_date", "true");
    else if (k === "source_image") params.set("has_source_image", "true");
  } else if (filter === "publicly_visible") {
    // Iter 20.17.6 — public-visibility helper.
    params.set("publicly_visible", "true");
  } else if (filter === "manual_check_only") {
    // Iter 20.17.6 — broken-link quarantine helper.
    params.set("manual_check_only", "true");
  } else if (DATA_STATUS_VALUES.has(filter)) {
    params.set("data_status", filter);
  } else if (VALIDATION_STATUS_VALUES.has(filter)) {
    params.set("validation_status", filter);
  } else {
    // Iter 20.17.6 — unknown values fall through with a clear marker
    // so the backend can return a 400 instead of silently returning 0.
    params.set("data_status", filter);
  }
  return params.toString();
}

export default function IntegrityQueue() {
  const [summary, setSummary] = useState(null);
  const [listMeta, setListMeta] = useState(null);
  const [comps, setComps] = useState([]);
  const [filter, setFilter] = useState("all");
  const [sort, setSort] = useState("newest");
  const [selected, setSelected] = useState(() => new Set());
  const [busy, setBusy] = useState(null);
  const { diag } = useDiagnostics();

  const refresh = async () => {
    const qs = buildAdminQuery(filter, sort);
    // Iter 20.17.6 — emit a debug-only console line so admins can see
    // which backend query the dropdown is actually firing.
    if (typeof window !== "undefined" && window.localStorage?.getItem("INTEGRITY_DEBUG") === "1") {
      // eslint-disable-next-line no-console
      console.info("[IntegrityQueue] filter=", filter, "→ qs=", qs || "(empty)");
    }
    const metaQs = qs ? `${qs}&include_meta=true` : "include_meta=true";
    const [s, c] = await Promise.all([
      api.get("/admin/integrity/audit-summary"),
      api.get(`/admin/competitions?${metaQs}`),
    ]);
    setSummary(s.data);
    setListMeta(Array.isArray(c.data) ? null : c.data);
    setComps(Array.isArray(c.data) ? c.data : (c.data.items || []));
  };

  useEffect(() => {
    refresh();
    // Backend now handles filtering/sorting, so refresh on every change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, sort]);

  // Clear selection whenever filter/sort change.
  useEffect(() => { setSelected(new Set()); }, [filter, sort]);

  const filtered = comps;  // backend-filtered now
  const operatorVisibilitySummary = useMemo(
    () => Object.values(listMeta?.operator_public_visibility_summary || {}),
    [listMeta],
  );

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((prev) =>
      prev.size === filtered.length
        ? new Set()
        : new Set(filtered.map((c) => c.id)),
    );
  };

  const validate = async (id) => {
    setBusy(id);
    try {
      const { data } = await api.post(`/admin/competitions/${id}/validate-source-url`);
      toast.success(
        `Validated → ${data.source_url_validation_status} · ${data.competition_data_status}`,
      );
      refresh();
    } catch (e) {
      toast.error("Validation failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const setStatus = async (id, status) => {
    const label = status === "verified_live" ? "Promote to verified_live" : `Mark as ${status}`;
    if (!confirmProductionWrite(diag, `${label}.`)) return;
    setBusy(id);
    try {
      await api.post(`/admin/competitions/${id}/data-status`, {
        competition_data_status: status,
      });
      toast.success(`Marked as ${status}`);
      refresh();
    } catch (e) {
      toast.error("Update failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const verifyLive = async (id) => {
    if (!confirmProductionWrite(diag, "Review / Verify this source record as live. This does not publish it.")) return;
    setBusy(id);
    try {
      await api.post(`/admin/competitions/${id}/verify-live`);
      toast.success("Marked verified live");
      refresh();
    } catch (e) {
      toast.error("Verify failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const publishCompetition = async (row) => {
    const state = publishStatusForCompetition(row);
    if (!state.canPublish) {
      const reasons = state.blockers.length
        ? state.blockers.map(publicMissingReasonLabel).join(", ")
        : state.publishTitle;
      toast.error(`Publish blocked: ${reasons}`);
      return;
    }
    if (!confirmProductionWrite(diag, "Publish this verified competition to the public listings.")) return;
    setBusy(row.id);
    try {
      const { data } = await api.post(`/admin/competitions/${row.id}/publish`);
      toast.success("Published");
      setComps((prev) => prev.map((c) => (c.id === row.id ? data : c)));
      refresh();
    } catch (e) {
      const detail = e.response?.data?.detail;
      if (detail?.missing_reasons?.length) {
        toast.error(
          `Publish blocked: ${detail.missing_reasons.map(publicMissingReasonLabel).join(", ")}`,
        );
      } else {
        toast.error("Publish failed: " + (detail || e.message));
      }
    } finally {
      setBusy(null);
    }
  };

  const bulkValidate = async () => {
    if (!confirmProductionWrite(diag, "Bulk-validate all pending source URLs (writes validation status back to each record).")) return;
    setBusy("bulk");
    try {
      const { data } = await api.post("/admin/integrity/bulk-validate", null, {
        params: { limit: 25, only_pending: true },
      });
      toast.success(`Bulk validated ${data.checked} competitions`);
      refresh();
    } catch (e) {
      toast.error("Bulk validation failed");
    } finally {
      setBusy(null);
    }
  };

  const bulkSetStatus = async (status) => {
    if (selected.size === 0) {
      toast.error("Select at least one row first.");
      return;
    }
    const label = status === "verified_live" ? "Review / Verify selected as live" : `Bulk mark ${selected.size} as ${status}`;
    if (!confirmProductionWrite(diag, `${label}.`)) return;
    setBusy("bulk-status");
    try {
      const { data } = await api.post("/admin/integrity/bulk-data-status", {
        ids: Array.from(selected),
        competition_data_status: status,
        note: status === "hidden" ? "Bulk hidden via Data Integrity panel" : undefined,
      });
      toast.success(`Updated ${data.updated_count} competition${data.updated_count === 1 ? "" : "s"}`);
      setSelected(new Set());
      refresh();
    } catch (e) {
      toast.error("Bulk status update failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  const rerunPrefill = async (id) => {
    setBusy(id);
    try {
      // Iter 20: prefer deep odds re-extraction. Mines the live page HTML
      // for ticket price / max tickets / draw date before falling back to
      // the asset resolver / candidate prefill.
      const row = filtered.find((c) => c.id === id);
      const candId = row?.candidate_id;
      const deep = await api.post(
        "/admin/integrity/rerun-deep-extraction",
        { competition_ids: [id], dry_run: false },
      );
      const result = deep.data?.results?.[0];
      if (result?.status === "extracted" && result.merged_fields?.length) {
        toast.success(`Re-extracted ${result.merged_fields.join(", ")}`);
      } else if (result?.status === "skipped_not_detail") {
        // Page is a listing / brand / FAQ — fall back to candidate prefill
        // (which understands non-competition pages too).
        if (candId) {
          await api.post(`/admin/candidates/${candId}/prefill`);
          toast.success("Ran candidate prefill (not a detail page)");
        } else {
          toast.info(`Not a live competition detail page (${result.page_type})`);
        }
      } else if (result?.status === "fetch_failed") {
        toast.error(`Fetch failed (HTTP ${result.http_status || "0"})`);
      } else {
        toast.info(`No new fields extracted (${result?.status || "no result"})`);
      }
      refresh();
    } catch (e) {
      toast.error("Re-run failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setBusy(null);
    }
  };

  // Render evidence badges for the row's field_sources (compact, max 5).
  const renderEvidenceBadges = (row) => {
    const fs = row.field_sources || {};
    const sources = new Set();
    Object.values(fs).forEach((f) => {
      if (f && f.source) sources.add(f.source);
    });
    if (sources.size === 0) return null;
    return Array.from(sources).slice(0, 5).map((s) => (
      <span
        key={s}
        className="px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-white/[0.06] text-white/70 border border-white/10"
        title={`Evidence source: ${s}`}
      >
        {EVIDENCE_LABELS[s] || s}
      </span>
    ));
  };

  const renderMissingFlags = (row) => {
    const flags = row.missing_data_flags || [];
    if (flags.length === 0) return null;
    return (
      <span
        className="text-[10px] text-red-300/80"
        title={`Missing: ${flags.join(", ")}`}
      >
        Missing {flags.length}
      </span>
    );
  };

  const renderPublicChecklist = (row) => {
    const checks = row.public_visibility?.diagnostics?.checks || {};
    const cap = checks.trusted_total_cap || {};
    const items = [
      ["Ticket", checks.ticket_price?.present ? "present" : "missing"],
      ["Date", checks.date?.present ? "present" : "missing"],
      ["Image", checks.image?.present ? "present" : "missing"],
      ["URL", checks.source_url?.valid ? "valid" : "invalid"],
      ["State", (checks.expired_or_past_date?.blocked || checks.closed_or_sold_out?.blocked) ? "blocked" : "open"],
    ];
    return (
      <div className="text-[10px] text-white/45 leading-snug max-w-[240px]">
        {items.map(([label, value]) => `${label}: ${value}`).join(" · ")}
        <br />
        Cap: {cap.present ? `trusted ${cap.total_ticket_cap || ""}`.trim() : "unavailable warning"}
      </div>
    );
  };

  const renderPublicState = (row) => {
    const state = publishStatusForCompetition(row);
    const diagnostics = row.public_visibility?.diagnostics || {};
    return (
      <div className="space-y-1.5" data-testid={`integrity-public-state-${row.id}`}>
        <span
          className={`inline-flex px-2 py-0.5 rounded border text-[10px] uppercase tracking-wider font-semibold ${state.tone}`}
          title={state.publishTitle}
        >
          {state.label}
        </span>
        <div className="text-[10px] text-white/45 leading-relaxed">
          Review: {diagnostics.verification_status || "pending_review"}<br />
          Data: {state.verificationLabel || "unknown"}<br />
          Publish: {state.publishLabel || "draft"}<br />
          Category: {diagnostics.category || row.category || "unknown"}
        </div>
        {renderPublicChecklist(row)}
        {state.blockers.length > 0 && (
          <div className="text-[10px] text-red-200/90 leading-snug max-w-[220px]">
            {state.blockers.slice(0, 3).map(publicMissingReasonLabel).join(", ")}
            {state.blockers.length > 3 ? ` +${state.blockers.length - 3} more` : ""}
          </div>
        )}
        {state.blockers.length === 0 && state.warningReasons?.length > 0 && (
          <div className="text-[10px] text-amber-200/90 leading-snug max-w-[220px]">
            {state.warningReasons.slice(0, 3).map(publicMissingReasonLabel).join(", ")}
            {state.warningReasons.length > 3 ? ` +${state.warningReasons.length - 3} more` : ""}
          </div>
        )}
      </div>
    );
  };

  if (!summary) return <div className="text-white/60">Loading audit summary…</div>;

  return (
    <div className="space-y-6" data-testid="admin-integrity-queue">
      <ClassifierCleanupCard
        diag={diag}
        onChange={() => { refresh(); }}
      />

      <Iter20CleanupCard onChange={() => { refresh(); }} />

      <div className="rounded-lg border border-sky-500/25 bg-sky-500/[0.06] p-4">
        <div className="text-sm font-semibold text-sky-100">Review -&gt; Verify -&gt; Publish</div>
        <p className="text-[11px] text-white/65 mt-1 leading-relaxed">
          Verified means the record has passed Data Integrity review. Published means it is visible on the public competitions page.
          Records must pass the public readiness gate before publishing.
        </p>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {Object.entries(summary.by_data_status || {}).map(([k, v]) => (
          <div
            key={k}
            className={`rounded-md px-3 py-2 text-xs border border-white/10 ${STATUS_STYLES[k] || "bg-white/[0.04] text-white/70"}`}
            data-testid={`integrity-stat-${k}`}
          >
            <div className="font-mono-num text-lg font-semibold">{v}</div>
            <div className="capitalize">{k.replace(/_/g, " ")}</div>
          </div>
        ))}
        <div className="rounded-md px-3 py-2 text-xs border border-white/10 bg-[var(--cc-gold)]/10 text-[var(--cc-gold)]" data-testid="integrity-stat-public">
          <div className="font-mono-num text-lg font-semibold">{summary.publicly_visible}</div>
          <div>Publicly visible</div>
        </div>
      </div>

      {operatorVisibilitySummary.length > 0 && (
        <div
          className="rounded-lg border border-white/10 bg-white/[0.03] p-4"
          data-testid="integrity-operator-visibility-summary"
        >
          <div className="text-sm font-semibold text-white/85">Public visibility by operator</div>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-2">
            {operatorVisibilitySummary.map((op) => (
              <div
                key={op.operator}
                className="rounded border border-white/10 bg-black/10 px-3 py-2 text-xs text-white/65"
                data-testid={`integrity-operator-summary-${op.operator}`}
              >
                <div className="font-semibold text-white/85">{op.operator}</div>
                <div className="mt-1">
                  {op.public_visible_count} public / {op.total_records} total
                </div>
                <div className="mt-1 text-[10px] leading-relaxed text-white/50">
                  Not verified {op.hidden_not_verified} · Draft {op.hidden_draft_unpublished} · Expired {op.hidden_expired_past_date}
                  <br />
                  Missing fields {op.hidden_missing_required_public_field} · Closed/sold out {op.hidden_closed_sold_out}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Controls */}
      <div className="flex flex-wrap gap-3 items-center">
        <select
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          data-testid="integrity-filter"
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white"
        >
          {FILTERS.map((f) => (
            <option key={f.value} value={f.value} className="bg-[var(--cc-navy-2)] text-white">
              {f.label}
            </option>
          ))}
        </select>
        <select
          value={sort}
          onChange={(e) => setSort(e.target.value)}
          data-testid="integrity-sort"
          className="bg-white/5 border border-white/15 rounded px-3 py-2 text-sm text-white"
        >
          {SORTS.map((s) => (
            <option key={s.value} value={s.value} className="bg-[var(--cc-navy-2)] text-white">
              Sort · {s.label}
            </option>
          ))}
        </select>
        <button
          onClick={bulkValidate}
          disabled={busy === "bulk"}
          data-testid="integrity-bulk-validate-btn"
          className="btn-gold px-4 py-2 rounded text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
        >
          <ArrowsClockwise size={14} weight="bold" /> Bulk validate pending
        </button>
        <span className="text-xs text-white/50">
          Showing {filtered.length} · {selected.size} selected
        </span>
        {selected.size > 0 && (
          <div className="inline-flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => bulkSetStatus("hidden")}
              disabled={busy === "bulk-status"}
              data-testid="integrity-bulk-hide"
              className="px-3 py-2 rounded text-xs border border-white/15 text-white/80 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5"
            >
              <Prohibit size={12} /> Bulk hide
            </button>
            <button
              type="button"
              onClick={() => bulkSetStatus("archived")}
              disabled={busy === "bulk-status"}
              data-testid="integrity-bulk-archive"
              className="px-3 py-2 rounded text-xs border border-white/15 text-white/80 hover:text-white hover:border-white/30 inline-flex items-center gap-1.5"
            >
              <ArchiveIcon size={12} /> Archive
            </button>
            <button
              type="button"
              onClick={() => bulkSetStatus("verified_live")}
              disabled={busy === "bulk-status"}
              data-testid="integrity-bulk-promote"
              className="px-3 py-2 rounded text-xs bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 inline-flex items-center gap-1.5"
            >
              <CheckCircle size={12} /> Review / Verify
            </button>
          </div>
        )}
      </div>

      {/* Table — iter 20.11: bounded max-height keeps the horizontal
          scrollbar visible at the bottom of the viewport, so the right-
          hand action column is reachable without scrolling to the very
          bottom of the page. */}
      <div
        className="rounded-lg border border-white/10 overflow-auto"
        style={{ maxHeight: "calc(100vh - 280px)" }}
        data-testid="integrity-table-scroll-wrapper"
      >
        <table className="w-full text-sm min-w-[1240px]">
          <thead className="bg-white/5 text-white/60">
            <tr>
              <th className="w-8 px-3 py-3">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && selected.size === filtered.length}
                  onChange={toggleAll}
                  aria-label="Select all"
                  data-testid="integrity-select-all"
                />
              </th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">URL</th>
              <th className="text-left px-4 py-3">Readiness</th>
              <th className="text-left px-4 py-3">Evidence</th>
              <th className="text-left px-4 py-3">Validation</th>
              <th className="text-left px-4 py-3">Data status</th>
              <th className="text-left px-4 py-3">Public state</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((c) => {
              const publishState = publishStatusForCompetition(c);
              const runPrimaryAction = () => {
                if (publishState.canVerify) verifyLive(c.id);
                else publishCompetition(c);
              };
              return (
                <tr
                  key={c.id}
                  className="border-t border-white/5 align-top"
                  data-testid={`integrity-row-${c.id}`}
                >
                <td className="px-3 py-3">
                  <input
                    type="checkbox"
                    checked={selected.has(c.id)}
                    onChange={() => toggleOne(c.id)}
                    data-testid={`integrity-select-${c.id}`}
                    aria-label={`Select ${c.title}`}
                  />
                </td>
                <td className="px-4 py-3 max-w-[260px]">
                  <div className="truncate font-semibold">{c.title}</div>
                  <div className="text-[11px] text-white/40 truncate flex items-center gap-2">
                    <span>{c.public_visibility?.diagnostics?.operator || c.source_operator_name || c.operator_name || c.operator_id || "Unknown operator"}</span>
                    <span>{c.source_discovery_method || c.ingest_source || "—"}</span>
                    {renderMissingFlags(c)}
                  </div>
                </td>
                <td className="px-4 py-3 max-w-[260px]">
                  <a
                    href={c.source_competition_url || c.competition_url}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="text-[var(--cc-gold)] underline text-[11px] truncate block"
                  >
                    {c.source_competition_url || c.competition_url || "—"}
                  </a>
                  {c.source_url_final_url &&
                    c.source_url_final_url !== (c.source_competition_url || c.competition_url) && (
                      <div className="text-[10px] text-white/40 truncate">
                        → {c.source_url_final_url} ({c.source_url_http_status})
                      </div>
                    )}
                </td>
                <td className="px-4 py-3">
                  {c.readiness_band ? (
                    <span
                      className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${READINESS_STYLES[c.readiness_band] || "bg-white/10 text-white/70"}`}
                      title={`Completeness ${c.completeness_score ?? "—"} · Prefill conf ${c.prefill_confidence_score ?? "—"}`}
                      data-testid={`integrity-readiness-${c.id}`}
                    >
                      {c.readiness_band.replace(/_/g, " ")} · {c.verification_readiness_score ?? "—"}
                    </span>
                  ) : (
                    <span className="text-[10px] text-white/30">—</span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <div
                    className="flex flex-wrap gap-1"
                    data-testid={`integrity-evidence-${c.id}`}
                  >
                    {renderEvidenceBadges(c) || (
                      <span className="text-[10px] text-white/30">—</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${VALIDATION_STYLES[c.source_url_validation_status] || "bg-white/10 text-white/70"}`}
                  >
                    {c.source_url_validation_status || "pending_review"}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`px-2 py-0.5 rounded text-[10px] uppercase tracking-wider font-semibold ${STATUS_STYLES[c.competition_data_status] || "bg-white/10 text-white/70"}`}
                  >
                    {c.competition_data_status || "source_unverified"}
                  </span>
                  <div className="text-[10px] text-white/45 mt-1">
                    {c.publish_status || "draft"}
                  </div>
                </td>
                <td className="px-4 py-3">
                  {renderPublicState(c)}
                </td>
                <td className="px-4 py-3 text-right whitespace-nowrap">
                  <button
                    type="button"
                    onClick={runPrimaryAction}
                    disabled={busy === c.id || (!publishState.canPublish && !publishState.canVerify)}
                    data-testid={`integrity-publish-${c.id}`}
                    className="px-2 py-1 rounded text-[11px] font-semibold border border-sky-400/35 text-sky-100 hover:bg-sky-500/20 disabled:opacity-40 disabled:hover:bg-transparent mr-1"
                    title={publishState.canVerify ? "Review / Verify this source record as live" : publishState.publishTitle}
                  >
                    {publishState.primaryActionLabel || "Publish"}
                  </button>
                  <button
                    type="button"
                    onClick={() => rerunPrefill(c.id)}
                    disabled={busy === c.id}
                    data-testid={`integrity-prefill-${c.id}`}
                    className="text-[var(--cc-gold)] hover:text-amber-300 p-1.5 inline-flex"
                    title="Re-run evidence-assisted prefill"
                  >
                    <Sparkle size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => validate(c.id)}
                    disabled={busy === c.id}
                    data-testid={`integrity-validate-${c.id}`}
                    className="text-white/70 hover:text-white p-1.5 inline-flex items-center gap-1"
                    title="Validate source URL"
                  >
                    <ArrowsClockwise size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => verifyLive(c.id)}
                    disabled={busy === c.id}
                    data-testid={`integrity-promote-${c.id}`}
                    className="text-emerald-400 hover:text-emerald-300 p-1.5"
                    title="Review / Verify as live without publishing"
                  >
                    <CheckCircle size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus(c.id, "broken_link")}
                    disabled={busy === c.id}
                    data-testid={`integrity-broken-${c.id}`}
                    className="text-red-400 hover:text-red-300 p-1.5"
                    title="Mark broken link"
                  >
                    <Warning size={14} />
                  </button>
                  <button
                    type="button"
                    onClick={() => setStatus(c.id, "hidden")}
                    disabled={busy === c.id}
                    data-testid={`integrity-hide-${c.id}`}
                    className="text-slate-300 hover:text-white p-1.5"
                    title="Hide from public"
                  >
                    <Prohibit size={14} />
                  </button>
                  <a
                    href={`/competition/${c.slug}`}
                    target="_blank"
                    rel="noreferrer"
                    className="text-white/60 hover:text-white p-1.5 inline-flex"
                    title="Open public page"
                  >
                    <Eye size={14} />
                  </a>
                </td>
                </tr>
              );
            })}
            {filtered.length === 0 && (
              <tr>
                <td colSpan={9} className="px-4 py-8 text-center text-white/40">
                  Nothing matches.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// ---------------- Classifier cleanup (iter 17) ----------------

const CATEGORY_LABELS = {
  live_competition: "Live detail pages (keep)",
  listing_requiring_extraction: "Listing/category pages (extract children)",
  expired_competition: "Expired competitions",
  operator_evidence: "Operator evidence",
  non_competition: "FAQ / how-to / past winners / terms / about / contact / blog",
  uncertain: "Uncertain (keep, manual review)",
};

const CATEGORY_TONE = {
  live_competition: "bg-emerald-500/15 text-emerald-200 border-emerald-500/30",
  listing_requiring_extraction: "bg-indigo-500/15 text-indigo-200 border-indigo-500/30",
  expired_competition: "bg-amber-500/15 text-amber-200 border-amber-500/30",
  operator_evidence: "bg-sky-500/15 text-sky-200 border-sky-500/30",
  non_competition: "bg-red-500/15 text-red-200 border-red-500/30",
  uncertain: "bg-slate-500/15 text-slate-200 border-slate-500/30",
};

function ClassifierCleanupCard({ diag, onChange }) {
  const { diag: liveDiag } = useDiagnostics();
  const [open, setOpen] = useState(false);
  const [useLlm, setUseLlm] = useState(false);
  const [onlyBrave, setOnlyBrave] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(null);

  const mode = liveDiag?.candidate_classifier_mode || diag?.candidate_classifier_mode;

  const runDryRun = async () => {
    setBusy("dry");
    try {
      const { data } = await api.post(
        `/admin/classify/dry-run?only_brave=${onlyBrave}&use_llm=${useLlm}`,
      );
      setPreview(data);
      toast.success(`Dry-run scanned ${data.scanned_count} records`);
    } catch (e) {
      toast.error("Dry-run failed: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(null); }
  };

  const apply = async () => {
    if (!confirmProductionWrite(liveDiag, "Apply the classifier cleanup: archive non-competition pages (FAQ / how-to / past winners / terms / about / contact / blog), archive listing/brand/category pages (children will be re-discovered on next Brave run), and set expired competitions to expired. Live detail pages and uncertain records are NEVER changed.")) return;
    setBusy("apply");
    try {
      // Iter 20.16 — Plan-based apply. The dry-run stores a server-side
      // plan; apply just replays it (no second OpenAI call, no risk of
      // 5-min proxy timeout). Falls back to legacy mode only if a plan_id
      // is unavailable (older preview deploys).
      const payload = {
        only_brave: onlyBrave,
        use_llm: false,            // never re-classify in apply
        apply_categories: [
          "non_competition", "operator_evidence", "expired_competition",
          "listing_requiring_extraction",
        ],
      };
      if (preview?.cleanup_plan_id) {
        payload.cleanup_plan_id = preview.cleanup_plan_id;
      }
      const { data } = await api.post(
        "/admin/classify/apply",
        payload,
        { timeout: 2 * 60 * 1000 },
      );
      const totalChanged = Object.values(data.changed || {}).reduce((a, b) => a + b, 0);
      const errCount = (data.errors || []).length;
      const sk = data.skipped || {};
      const detailParts = [];
      if (totalChanged) detailParts.push(`${totalChanged} updated`);
      if (sk.already_applied) detailParts.push(`${sk.already_applied} already-applied`);
      if (sk.classify_failed) detailParts.push(`${sk.classify_failed} classify-failed`);
      if (errCount) detailParts.push(`${errCount} update errors`);
      const headline = totalChanged
        ? `Cleaned up ${totalChanged} record${totalChanged === 1 ? "" : "s"}`
        : "Apply ran but changed 0 records";
      toast.success(detailParts.length ? `${headline} — ${detailParts.join(", ")}` : headline);
      setPreview(null);
      onChange?.();
    } catch (e) {
      // Iter 20.15 — surface the actual HTTP status / response body
      // instead of axios's generic "XHR request failed".
      const status = e.response?.status;
      const detail = e.response?.data?.detail
        || e.response?.data?.error
        || (typeof e.response?.data === "string" ? e.response.data : null)
        || e.message
        || "Unknown error";
      const where = status ? `HTTP ${status}` : (e.code || "network");
      const endpoint = "/admin/classify/apply";
      toast.error(`Apply failed (${where} on ${endpoint}): ${detail}`);
      // eslint-disable-next-line no-console
      console.error("[classifier-apply] failed", { status, code: e.code, endpoint, response: e.response, error: e });
    } finally { setBusy(null); }
  };

  return (
    <div
      className="rounded-lg border border-amber-500/30 bg-amber-500/[0.05] p-4"
      data-testid="classifier-cleanup-card"
    >
      <div className="flex items-start gap-3">
        <Sparkle size={18} weight="duotone" className="text-amber-300 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-amber-100">
            Candidate classifier cleanup
            <span className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-white/10 text-white/70">
              {mode || "unknown"}
            </span>
          </div>
          <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
            Brave Search indexes everything an operator publishes — FAQ pages, past winners, how-to-enter,
            terms. Run a dry-run to see how many discovered records aren't real competitions, then apply
            the plan to move them out of Data Integrity into the candidate / operator evidence pool.
            <strong className="text-white"> Live competitions and uncertain records are never auto-changed.</strong>
          </p>
          <div className="mt-3 flex items-center gap-3 flex-wrap">
            <label className="inline-flex items-center gap-2 text-[12px] text-white/80">
              <input
                type="checkbox"
                checked={onlyBrave}
                onChange={(e) => setOnlyBrave(e.target.checked)}
                data-testid="classifier-only-brave"
              />
              Only Brave-discovered records
            </label>
            <label className="inline-flex items-center gap-2 text-[12px] text-white/80">
              <input
                type="checkbox"
                checked={useLlm}
                onChange={(e) => setUseLlm(e.target.checked)}
                disabled={mode !== "llm_enabled"}
                data-testid="classifier-use-llm"
              />
              Use OpenAI for uncertain cases {mode !== "llm_enabled" && (
                <span className="text-[10px] text-white/40">(configure key in Providers)</span>
              )}
            </label>
            <button
              type="button"
              onClick={runDryRun}
              disabled={busy === "dry"}
              data-testid="classifier-dry-run-btn"
              className="ml-auto px-3 py-1.5 text-xs font-semibold rounded bg-white/10 hover:bg-white/15 text-white inline-flex items-center gap-1.5 disabled:opacity-50"
            >
              <ArrowsClockwise size={12} /> {busy === "dry" ? "Scanning…" : "Run dry-run"}
            </button>
            {preview && (
              <button
                type="button"
                onClick={() => setOpen((o) => !o)}
                className="text-[12px] text-amber-200 underline"
              >
                {open ? "Hide" : "Show"} preview
              </button>
            )}
          </div>
        </div>
      </div>

      {preview && open && (
        <div className="mt-4 space-y-3" data-testid="classifier-preview">
          <div className="text-[11px] text-white/60">
            Scanned <span className="text-white">{preview.scanned_count}</span> record{preview.scanned_count === 1 ? "" : "s"}
            {preview.use_llm ? " · with LLM refinement" : " · deterministic only"}
            {preview.cleanup_plan_id && (
              <span
                className="ml-2 px-1.5 py-0.5 rounded text-[9px] uppercase tracking-wider font-semibold bg-white/10 text-white/70"
                title={`Plan expires at ${preview.plan_expires_at}`}
                data-testid="classifier-plan-id"
              >
                plan {preview.cleanup_plan_id.slice(0, 8)}
              </span>
            )}
          </div>
          {preview.cost_preview && (
            <div
              className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[10px] text-white/65"
              data-testid="classifier-cost-preview"
            >
              <div className="rounded border border-white/10 px-2 py-1.5">
                <div className="uppercase tracking-wider opacity-60">Deterministic</div>
                <div className="text-white font-mono-num text-sm">{preview.cost_preview.deterministic_decisions}</div>
              </div>
              <div className="rounded border border-white/10 px-2 py-1.5">
                <div className="uppercase tracking-wider opacity-60">LLM calls used</div>
                <div className="text-white font-mono-num text-sm">{preview.cost_preview.llm_calls_used} / {preview.cost_preview.max_llm_calls}</div>
              </div>
              <div className="rounded border border-white/10 px-2 py-1.5">
                <div className="uppercase tracking-wider opacity-60">Cache hits</div>
                <div className="text-white font-mono-num text-sm">{preview.cost_preview.cache_hits}</div>
              </div>
              <div className="rounded border border-white/10 px-2 py-1.5">
                <div className="uppercase tracking-wider opacity-60">Est. cost</div>
                <div className="text-white font-mono-num text-sm">${preview.cost_preview.approx_cost_usd_estimate ?? 0}</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2">
            {["live_competition", "listing_requiring_extraction", "expired_competition", "operator_evidence", "non_competition", "uncertain"].map((cat) => (
              <div
                key={cat}
                className={`rounded p-2 border ${CATEGORY_TONE[cat]}`}
                data-testid={`classifier-bucket-${cat}`}
              >
                <div className="text-[10px] uppercase font-semibold tracking-wider opacity-80">
                  {CATEGORY_LABELS[cat]}
                </div>
                <div className="text-2xl font-semibold mt-0.5">{preview.totals?.[cat] ?? 0}</div>
                {preview.examples?.[cat]?.slice(0, 3).map((ex) => (
                  <div key={ex.id} className="text-[10px] truncate mt-1 opacity-80" title={ex.url}>
                    · {ex.title}
                  </div>
                ))}
              </div>
            ))}
          </div>
          <div className="text-[11px] text-white/60 leading-relaxed">
            Apply will archive <strong>non-competition</strong>, <strong>operator evidence</strong>,
            and <strong>listing/category pages</strong> (children will be re-discovered on the next
            Brave run), and set <strong>expired</strong> on past-draw competitions.
            Live detail pages and uncertain records stay in <code>source_pending_review</code> for manual review.
          </div>
          <button
            type="button"
            onClick={apply}
            disabled={busy === "apply"}
            data-testid="classifier-apply-btn"
            className="px-3 py-1.5 text-xs font-semibold rounded bg-amber-500/30 hover:bg-amber-500/40 text-amber-100 inline-flex items-center gap-1.5 disabled:opacity-50"
          >
            <CheckCircle size={12} weight="bold" /> {busy === "apply" ? "Applying…" : "Apply cleanup"}
          </button>
        </div>
      )}
    </div>
  );
}


// -------------------- Iter 20: cleanup + deep extraction --------------------

const ITER20_ACTIONS = [
  {
    key: "archive_listing",
    label: "Archive listing pollution",
    endpoint: "/admin/integrity/archive-listing-pollution",
    body: () => ({ dry_run: true }),
    description:
      "Find existing competitions whose URL is actually a listing / brand / category / index page (e.g. /competitions/mercedes) and archive them.",
    applyBody: () => ({ dry_run: false }),
    confirm:
      "Archive listing/brand/category records that were wrongly saved as competitions. Records keep full provenance — they are NOT deleted.",
  },
  {
    key: "dedupe_generic",
    label: "Dedupe generic stubs",
    endpoint: "/admin/integrity/dedupe-generic-competitions",
    body: () => ({ dry_run: true }),
    description:
      'Find generic-stub competitions (titles like "7days Performance" or "Mercedes") and archive duplicates / empty stubs.',
    applyBody: () => ({ dry_run: false }),
    confirm:
      "Archive duplicate / empty generic-stub competitions. Highest-confidence record per stub is kept.",
  },
  {
    key: "deep_extract",
    label: "Re-run deep odds extraction",
    endpoint: "/admin/integrity/rerun-deep-extraction",
    body: () => ({ dry_run: true, only_missing_odds: true, limit: 50 }),
    description:
      "Re-fetch live competition pages where ticket_price / max_tickets / draw_date is missing and run the iter-20 layered odds extractor (JSON-LD, inline state, meta, data attrs, visible labels). Admin-edited fields are never overwritten.",
    applyBody: () => ({ dry_run: false, only_missing_odds: true, limit: 50 }),
    confirm:
      "Re-extract odds from up to 50 live competition pages with missing odds. Admin edits are preserved.",
  },
  // Iter 20.17 — title hygiene + Click broken-link quarantine.
  {
    key: "normalise_titles",
    label: "Normalise generic titles",
    endpoint: "/admin/integrity/normalise-titles",
    body: () => ({ dry_run: true, limit: 500 }),
    description:
      "Re-title rows whose title is generic (Facebook, operator name, empty). Title is derived from the URL slug. Original title kept in title_source_history.",
    applyBody: () => ({ dry_run: false, limit: 500 }),
    confirm:
      "Re-title generic rows (Facebook, operator-name only) from the URL slug. Status is NOT changed. Records are NOT deleted.",
  },
  {
    key: "quarantine_click",
    label: "Quarantine Click 404 rows",
    endpoint: "/admin/integrity/quarantine-broken-click",
    body: () => ({ dry_run: true, limit: 500 }),
    description:
      "Quarantine Click /competition/<slug>/ rows that already validate as broken/404 — set source_unverified + needs_link_manual_check so they leave normal review. Never deleted.",
    applyBody: () => ({ dry_run: false, limit: 500 }),
    confirm:
      "Quarantine broken Click /competition/ 404 rows into source_unverified + needs_link_manual_check. Records are NOT deleted.",
  },
  {
    key: "detect_closed",
    label: "Detect closed/expired competitions",
    endpoint: "/admin/integrity/detect-closed-competitions",
    body: () => ({ dry_run: true, limit: 500 }),
    description:
      "Find rows whose title / description / URL says the competition has closed, ended, drawn, sold out, or is past — and move them to 'expired'. Active CTAs override.",
    applyBody: () => ({ dry_run: false, limit: 500 }),
    confirm:
      "Move closed/ended/drawn/sold-out competitions out of normal review and set competition_data_status=expired. Records are NOT deleted; provenance is preserved.",
  },
  {
    key: "judge_page_role",
    label: "Post-fetch page-role judge",
    endpoint: "/admin/integrity/judge-page-role",
    // body() callbacks receive the current judge operator scope so the
    // request always reflects the currently-selected chip.
    body: (scope) => ({
      dry_run: true,
      limit: 200,
      fetch_html: true,
      apply_role_decisions: false,
      operator: scope || undefined,
    }),
    description:
      "Fetch each row's source URL once and classify its actual page role from visible content: live_competition_detail / past_winner_result / expired_competition / competition_listing_page / competition_category / operator_info / broken_or_stale_indexed_page / unknown_needs_review. Use the operator scope chips to target Click or DCG only.",
    applyBody: (scope) => ({
      dry_run: false,
      limit: 200,
      fetch_html: true,
      apply_role_decisions: true,
      operator: scope || undefined,
    }),
    confirm:
      "Fetch each row's source URL once, classify the page role, and apply role-driven status changes (winner/expired → expired; broken/404 → source_unverified+manual_check; listing/category → archived). Detail-slug signals override listing-text language. NEVER auto-promotes to verified_live.",
  },
];

// Iter 20.17.5 — operator-scope chips for targeted runs (stable keys
// matching backend operator_resolver). Empty value = no filter.
const OPERATOR_SCOPES = [
  { value: "", label: "All" },
  { value: "click", label: "Click" },
  { value: "dcg", label: "DCG" },
  { value: "botb", label: "BOTB" },
  { value: "7days", label: "7Days" },
  { value: "elite", label: "Elite" },
  { value: "feelgood", label: "FGC" },
  { value: "llf", label: "LLF" },
];

function Iter20CleanupCard({ onChange }) {
  const { diag } = useDiagnostics();
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [judgeScope, setJudgeScope] = useState("");

  // Iter 20.17.7 — Consolidated cleanup state.
  const [mode, setMode] = useState("quick");
  const [combinedResult, setCombinedResult] = useState(null);
  const [combinedBusy, setCombinedBusy] = useState(null);
  const [advancedOpen, setAdvancedOpen] = useState(false);

  const runCombined = async (asApply) => {
    if (asApply) {
      if (!combinedResult?.cleanup_plan_id) {
        toast.error("Run dry-run first — no cleanup_plan_id available.");
        return;
      }
      if (!confirmProductionWrite(
        diag,
        "Apply the combined cleanup plan: archive listing pollution, normalise titles, quarantine broken Click rows, expire closed competitions. Records are NEVER hard-deleted. Public/live records are NOT promoted.",
      )) return;
    }
    setCombinedBusy(asApply ? "apply" : "dry");
    try {
      const payload = asApply
        ? { dry_run: false, mode, cleanup_plan_id: combinedResult.cleanup_plan_id }
        : { dry_run: true, mode, limit: 1000 };
      const { data } = await api.post("/admin/integrity/cleanup", payload);
      if (asApply) {
        const total = Object.values(data.records_updated_by_action || {}).reduce((a, b) => a + b, 0);
        toast.success(
          `Cleanup applied: ${total} record(s) updated in ${data.duration_ms}ms.`,
        );
        setCombinedResult(null);
        onChange?.();
      } else {
        setCombinedResult(data);
        toast.success(
          `Cleanup dry-run: ${data.summary?.total_proposed_changes ?? 0} proposed change(s).`,
        );
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const detailStr = typeof detail === "object" ? JSON.stringify(detail) : (detail || e.message);
      toast.error(`Cleanup failed: ${detailStr}`);
    } finally {
      setCombinedBusy(null);
    }
  };

  const run = async (action, applyMode) => {
    if (applyMode && !confirmProductionWrite(diag, action.confirm)) {
      return;
    }
    setBusy(`${action.key}:${applyMode ? "apply" : "dry"}`);
    try {
      const scope = action.key === "judge_page_role" ? judgeScope : null;
      const body = applyMode ? action.applyBody(scope) : action.body(scope);
      const { data } = await api.post(action.endpoint, body);
      setResults((prev) => ({ ...prev, [action.key]: data }));
      if (applyMode) {
        const wrote =
          data.archived ?? data.merged_count ?? data.match_count ?? data.updated ?? 0;
        toast.success(`${action.label}: ${wrote} record(s) processed.`);
        onChange?.();
      } else {
        const matched = data.match_count ?? data.scanned_after_operator_filter ?? data.scanned ?? data.processed ?? 0;
        toast.success(`Dry-run: ${matched} match(es).`);
      }
    } catch (e) {
      const detail = e.response?.data?.detail;
      const detailStr =
        typeof detail === "object" ? JSON.stringify(detail) : (detail || e.message);
      toast.error(`${action.label} failed: ${detailStr}`);
    } finally {
      setBusy(null);
    }
  };

  return (
    <div
      className="rounded-lg border border-sky-500/30 bg-sky-500/[0.05] p-4"
      data-testid="iter20-cleanup-card"
    >
      {/* Iter 20.17.7 — Primary "Run Data Integrity Cleanup" header */}
      <div className="flex items-start gap-3">
        <Sparkle size={18} weight="duotone" className="text-sky-300 mt-0.5" />
        <div className="flex-1">
          <div className="text-sm font-semibold text-sky-100">
            Run Data Integrity Cleanup
          </div>
          <p className="text-[11px] text-white/60 mt-1 leading-relaxed">
            Combined deterministic cleanup pass. Quick mode skips fetches and
            LLM. Full review adds the post-fetch page-role judge and deep odds
            extraction. Custom lets you pick phases under Advanced. Dry-run
            first — apply gated by production-write confirmation. Records are
            never hard-deleted and never auto-promoted to verified_live.
          </p>
          <div className="mt-3 flex flex-wrap items-center gap-2" data-testid="cleanup-mode-row">
            <div className="flex gap-1" role="radiogroup" aria-label="Cleanup mode">
              {[
                { v: "quick", label: "Quick" },
                { v: "full_review", label: "Full Review" },
                { v: "custom", label: "Custom" },
              ].map((m) => (
                <button
                  key={m.v}
                  type="button"
                  onClick={() => setMode(m.v)}
                  data-testid={`cleanup-mode-${m.v}`}
                  className={`px-2.5 py-1 text-[11px] rounded font-semibold ${
                    mode === m.v
                      ? "bg-sky-500/40 text-sky-100"
                      : "bg-white/5 text-white/65 hover:bg-white/10"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <div className="flex gap-2 ml-auto">
              <button
                type="button"
                onClick={() => runCombined(false)}
                disabled={!!combinedBusy}
                data-testid="cleanup-dry-run-btn"
                className="px-2.5 py-1 text-[11px] font-semibold rounded bg-white/10 hover:bg-white/15 text-white disabled:opacity-50"
              >
                {combinedBusy === "dry" ? "Scanning…" : "Dry-run"}
              </button>
              <button
                type="button"
                onClick={() => runCombined(true)}
                disabled={!!combinedBusy || !combinedResult?.cleanup_plan_id}
                data-testid="cleanup-apply-btn"
                className="px-2.5 py-1 text-[11px] font-semibold rounded bg-sky-500/30 hover:bg-sky-500/40 text-sky-100 disabled:opacity-50"
              >
                {combinedBusy === "apply" ? "Applying…" : "Apply"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {combinedResult?.summary && (
        <div
          className="mt-3 rounded-md border border-white/10 bg-black/20 p-3 text-[11px] text-white/75 space-y-1.5"
          data-testid="cleanup-summary"
        >
          <div className="flex flex-wrap items-center gap-2 text-[10px]">
            <span className="text-white/50 uppercase tracking-wider">Result</span>
            <span className="text-white/90 font-mono-num">
              scanned {combinedResult.summary.scanned}
            </span>
            <span className="text-sky-300 font-mono-num">
              proposed {combinedResult.summary.total_proposed_changes}
            </span>
            <span className="text-white/55">
              unchanged {combinedResult.summary.left_unchanged}
            </span>
            {combinedResult.cleanup_plan_id && (
              <span className="ml-2 px-1.5 py-0.5 rounded bg-white/10 text-white/70 font-mono-num">
                plan {combinedResult.cleanup_plan_id.slice(0, 8)}
              </span>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-1">
            {Object.entries(combinedResult.summary.proposed_by_action || {}).map(([action, n]) => (
              <div
                key={action}
                className="rounded border border-white/10 px-2 py-1.5"
                data-testid={`cleanup-action-${action}`}
              >
                <div className="text-[9px] uppercase tracking-wider opacity-60">{action}</div>
                <div className="text-white font-mono-num text-sm">{n}</div>
              </div>
            ))}
          </div>
          {combinedResult.examples && Object.keys(combinedResult.examples).length > 0 && (
            <div className="space-y-1 mt-2 text-[10px]">
              {Object.entries(combinedResult.examples).map(([phase, exs]) => (
                <div key={phase}>
                  <span className="text-white/50 uppercase tracking-wider">{phase}</span>
                  {(exs || []).slice(0, 3).map((ex) => (
                    <div key={ex.id} className="truncate opacity-80 ml-3" title={ex.url}>
                      · {ex.title || ex.id?.slice(0, 8)}
                      <span className="ml-1 text-amber-300/80">[{ex.proposed_action}]</span>
                      <span className="ml-1 text-white/40">({ex.reason})</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Iter 20.17.7 — Advanced cleanup tools (collapsed by default) */}
      <div className="mt-4 border-t border-white/10 pt-3">
        <button
          type="button"
          onClick={() => setAdvancedOpen((v) => !v)}
          data-testid="advanced-cleanup-toggle"
          className="text-[11px] text-white/55 hover:text-white/80 font-semibold flex items-center gap-2"
        >
          <span>{advancedOpen ? "▾" : "▸"}</span>
          Advanced cleanup tools
          <span className="text-white/35 font-normal">
            ({ITER20_ACTIONS.length} individual actions)
          </span>
        </button>
        {advancedOpen && (
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="advanced-cleanup-grid">
            {ITER20_ACTIONS.map((action) => {
              const res = results[action.key];
              return (
                <div
                  key={action.key}
                  className="rounded-md border border-white/10 bg-black/20 p-3 flex flex-col"
                  data-testid={`iter20-action-${action.key}`}
                >
                  <div className="text-[12px] font-semibold text-white">{action.label}</div>
                  <p className="text-[11px] text-white/55 mt-1 leading-relaxed flex-1">
                    {action.description}
                  </p>
                  {action.key === "judge_page_role" && (
                    <div className="mt-2 flex flex-wrap gap-1" data-testid="judge-operator-scopes">
                      {OPERATOR_SCOPES.map((scope) => (
                        <button
                          key={scope.value || "all"}
                          type="button"
                          onClick={() => setJudgeScope(scope.value)}
                          data-testid={`judge-scope-${scope.value || "all"}`}
                          className={`px-1.5 py-0.5 text-[10px] rounded ${
                            (judgeScope || "") === (scope.value || "")
                              ? "bg-sky-500/40 text-sky-100"
                              : "bg-white/5 text-white/65 hover:bg-white/10"
                          }`}
                        >
                          {scope.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <div className="mt-2 flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => run(action, false)}
                      disabled={busy?.startsWith(action.key)}
                      data-testid={`iter20-${action.key}-dry-btn`}
                      className="px-2.5 py-1 text-[11px] font-semibold rounded bg-white/10 hover:bg-white/15 text-white disabled:opacity-50"
                    >
                      {busy === `${action.key}:dry` ? "Scanning…" : "Dry-run"}
                    </button>
                    <button
                      type="button"
                      onClick={() => run(action, true)}
                      disabled={busy?.startsWith(action.key)}
                      data-testid={`iter20-${action.key}-apply-btn`}
                      className="px-2.5 py-1 text-[11px] font-semibold rounded bg-sky-500/30 hover:bg-sky-500/40 text-sky-100 disabled:opacity-50"
                    >
                      {busy === `${action.key}:apply` ? "Applying…" : "Apply"}
                    </button>
                  </div>
                  {res && (
                    <div
                      className="mt-2 text-[10px] text-white/65 border-t border-white/5 pt-2 space-y-1"
                      data-testid={`iter20-${action.key}-result`}
                    >
                      <div>
                        <span className="opacity-60">
                          {res.dry_run ? "Dry-run" : "Applied"}:
                        </span>{" "}
                        <span className="text-white font-mono-num">
                          {res.match_count ?? res.processed ?? 0}
                        </span>{" "}
                        {res.dry_run ? "match(es)" : "scanned"}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
